import { FaRegEye } from "react-icons/fa";
import { IoLockOpenOutline, IoLockClosedOutline, IoCallOutline, IoCalendarOutline } from "react-icons/io5";
import { MdOutlineEmail } from "react-icons/md";
import { LuMapPin } from "react-icons/lu";
import { GoTrash } from "react-icons/go";
import { FiEdit2, FiBriefcase, FiKey } from "react-icons/fi";
import { strings } from "../../../strings/strings";

const CompanyCard = ({ company, onView, onEdit, onFreeze, onDelete, isOneColumn }) => {
  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    const formattedStr = typeof dateString === "string" ? dateString.replace(" ", "T") : dateString;
    const date = new Date(formattedStr);
    if (isNaN(date.getTime())) return dateString;
    return date.toLocaleString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const getInitials = (name) => {
    if (!name) return "??";
    const parts = name.trim().split(/\s+/);
    if (parts.length > 1) {
      return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
    }
    return parts[0].slice(0, 2).toUpperCase();
  };

  const formatAddress = (address) => {
    if (!address) return "-";
    const parts = [address.addressLine1, address.addressLine2, address.state, address.city, address.country].filter(
      (part) => part && part.trim() !== "",
    );

    if (parts.length === 0) return "-";

    const mainAddress = parts.join(", ");
    const zip = address.zipCode && address.zipCode.trim() !== "" ? ` (${address.zipCode})` : "";
    return mainAddress + zip;
  };

  return (
    <div
      className={`bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 hover:shadow-lg transition-all duration-200 overflow-hidden ${isOneColumn ? "flex flex-col sm:flex-row sm:items-center" : ""}`}
    >
      <div className={`${isOneColumn ? "flex-1" : ""} p-4 sm:p-6 ${!isOneColumn ? "pb-4" : ""}`}>
        <div className="flex items-start justify-start mb-4 gap-3 sm:gap-4">
          <div className="flex items-center gap-2 sm:gap-3 flex-1 min-w-0">
            <div className="flex-shrink-0">
              {company.companyLogo ? (
                <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-xl overflow-hidden shadow-sm border border-gray-100 dark:border-gray-700">
                  <img
                    src={company.companyLogo}
                    alt={company.companyName}
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      e.target.onerror = null;
                      e.target.src = "";
                    }}
                  />
                </div>
              ) : (
                <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-xl flex items-center justify-center bg-gradient-to-br from-primary/10 to-primary/5 dark:from-primary/20 dark:to-primary/10 shadow-sm border border-primary/20 dark:border-primary/30">
                  <span className="text-primary-dark dark:text-primary font-bold text-sm sm:text-base tracking-wider">
                    {getInitials(company.companyName)}
                  </span>
                </div>
              )}
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="font-bold text-lg sm:text-xl text-gray-900 dark:text-white truncate mb-1 sm:mb-1.5">
                {company.companyName}
              </h3>
              <span
                className={`inline-flex items-center px-2 sm:px-3 py-0.5 sm:py-1 text-xs font-semibold rounded-full ${
                  company.isActive
                    ? "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400"
                    : "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400"
                }`}
              >
                {!!company.isActive ? strings.ACTIVE : strings.SUSPENDED}
              </span>
            </div>
          </div>
        </div>

        {/* Details */}
        <div
          className={`${isOneColumn ? "grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-x-8 sm:gap-y-3.5" : "space-y-3"}`}
        >
          <div className="flex items-center gap-2.5 text-sm text-gray-600 dark:text-gray-400">
            <MdOutlineEmail className="w-4 h-4 flex-shrink-0 text-gray-500 dark:text-gray-500" />
            <span className="truncate font-medium">{company.companyEmail || "-"}</span>
          </div>
          <div className="flex items-center gap-2.5 text-sm text-gray-600 dark:text-gray-400">
            <IoCallOutline className="w-4 h-4 flex-shrink-0 text-gray-500 dark:text-gray-500" />
            <span className="font-medium">
              {company.mobileNumber ? `${company.mobileCode || ""} ${company.mobileNumber}`.trim() : "-"}
            </span>
          </div>
          <div className="flex items-center gap-2.5 text-sm text-gray-600 dark:text-gray-400">
            <FiKey className="w-4 h-4 flex-shrink-0 text-gray-500 dark:text-gray-500" />
            <span className="font-semibold tracking-wider">{company.accessCompanyCode || "-"}</span>
          </div>
          <div className="flex items-center gap-2.5 text-sm text-gray-600 dark:text-gray-400">
            <LuMapPin className="w-4 h-4 flex-shrink-0 text-gray-500 dark:text-gray-500" />
            <span className="truncate font-medium">{formatAddress(company?.companyAddress)}</span>
          </div>
          <div className="flex items-center gap-2.5 text-sm text-gray-600 dark:text-gray-400">
            <FiBriefcase className="w-4 h-4 flex-shrink-0 text-gray-500 dark:text-gray-500" />
            <span className="truncate font-medium">{company.industry || "-"}</span>
          </div>
          <div
            className="flex items-center gap-2.5 text-sm text-gray-600 dark:text-gray-400"
            title={`${strings.RECENT_ACTIVITY}: ${formatDate(company.recentActivity || company.recent_activity || company.createdAt)}`}
          >
            <IoCalendarOutline className="w-4 h-4 flex-shrink-0 text-gray-700 dark:text-gray-500" />
            <span className="truncate font-medium">
              <span className="text-gray-700 dark:text-gray-400 font-bold">{strings.RECENT_ACTIVITY}: </span>
              {formatDate(company.recentActivity || company.recent_activity || company.createdAt)}
            </span>
          </div>
        </div>
      </div>

      {isOneColumn && (
        <div className="flex sm:flex-col gap-2 sm:gap-2 px-4 py-3 sm:px-6 sm:py-6 bg-gray-50/50 dark:bg-gray-900/30 border-t sm:border-t-0 sm:border-l border-gray-200 dark:border-gray-700">
          <button
            onClick={() => onView(company)}
            className="flex-1 sm:flex-none group p-2 bg-primary/10 hover:bg-primary/20 dark:bg-primary/20 dark:hover:bg-primary/40 text-primary-dark dark:text-primary rounded-xl shadow-lg hover:shadow-xl transition-all duration-200 transform hover:scale-105"
            title={strings.VIEW_DETAILS}
          >
            <FaRegEye className="w-4 h-4 mx-auto" />
          </button>
          <button
            onClick={() => onEdit(company)}
            className="flex-1 sm:flex-none group p-2 bg-blue-100 hover:bg-blue-200 dark:bg-blue-900/20 dark:hover:bg-blue-900/40 text-blue-700 dark:text-blue-400  rounded-xl shadow-lg hover:shadow-xl transition-all duration-200 transform hover:scale-105"
            title={strings.EDIT_COMPANY}
          >
            <FiEdit2 className="w-4 h-4 mx-auto" />
          </button>
          <button
            onClick={() => onFreeze(company)}
            className="flex-1 sm:flex-none group p-2 bg-orange-100 hover:bg-orange-200 dark:bg-orange-900/20 dark:hover:bg-orange-900/40 text-orange-700 dark:text-orange-400 rounded-xl shadow-lg hover:shadow-xl transition-all duration-200 transform hover:scale-105"
            title={company.isActive ? strings.FREEZE_COMPANY : strings.UNFREEZE_COMPANY}
          >
            {company.isActive ? (
              <IoLockClosedOutline className="w-4 h-4 mx-auto" />
            ) : (
              <IoLockOpenOutline className="w-4 h-4 mx-auto" />
            )}
          </button>
          <button
            onClick={() => onDelete(company)}
            className="flex-1 sm:flex-none group p-2 bg-red-100 hover:bg-red-200 dark:bg-red-900/20 dark:hover:bg-red-900/40 text-red-700 dark:text-red-400 rounded-xl shadow-lg hover:shadow-xl transition-all duration-200 transform hover:scale-105"
            title={strings.DELETE_COMPANY}
          >
            <GoTrash className="w-4 h-4 mx-auto" />
          </button>
        </div>
      )}
      {!isOneColumn && (
        <div className="bg-gray-50 dark:bg-gray-900/50 px-4 py-3 border-t border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-center gap-2">
            <button
              onClick={() => onView(company)}
              className="flex-1 inline-flex items-center justify-center gap-1.5 px-2 py-2 bg-primary/5 hover:bg-primary/10 dark:bg-primary/20 dark:hover:bg-primary/40 text-primary-dark dark:text-primary rounded-lg border border-primary/20 dark:border-primary/40 transition-all duration-200 hover:shadow-sm text-xs font-medium"
              title={strings.VIEW_DETAILS}
            >
              <FaRegEye className="w-3.5 h-3.5" />
              <span className="hidden sm:inline">{strings.VIEW}</span>
            </button>
            <button
              onClick={() => onEdit(company)}
              className="flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-2 bg-blue-50 hover:bg-blue-100 dark:bg-blue-900/20 dark:hover:bg-blue-900/40 text-blue-700 dark:text-blue-400 rounded-lg border border-blue-200 dark:border-blue-800 transition-all duration-200 hover:shadow-sm text-xs font-medium"
              title={strings.EDIT_COMPANY}
            >
              <FiEdit2 className="w-3.5 h-3.5" />
              <span className="hidden sm:inline">{strings.EDIT}</span>
            </button>
            <button
              onClick={() => onFreeze(company)}
              className="flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-2 bg-orange-50 hover:bg-orange-100 dark:bg-orange-900/20 dark:hover:bg-orange-900/40 text-orange-700 dark:text-orange-400 rounded-lg border border-orange-200 dark:border-orange-800 transition-all duration-200 hover:shadow-sm text-xs font-medium"
              title={company.isActive ? strings.FREEZE_COMPANY : strings.UNFREEZE_COMPANY}
            >
              {company.isActive ? (
                <>
                  <IoLockClosedOutline className="w-3.5 h-3.5" />
                  <span className="hidden sm:inline">{strings.FREEZE}</span>
                </>
              ) : (
                <>
                  <IoLockOpenOutline className="w-3.5 h-3.5" />
                  <span className="hidden sm:inline">{strings.UNFREEZE}</span>
                </>
              )}
            </button>
            <button
              onClick={() => onDelete(company)}
              className="flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-2 bg-red-50 hover:bg-red-100 dark:bg-red-900/20 dark:hover:bg-red-900/40 text-red-700 dark:text-red-400 rounded-lg border border-red-200 dark:border-red-800 transition-all duration-200 hover:shadow-sm text-xs font-medium"
              title={strings.DELETE_COMPANY}
            >
              <GoTrash className="w-3.5 h-3.5" />
              <span className="hidden sm:inline">{strings.DELETE}</span>
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default CompanyCard;
